import { AssetAmount, AssetRatio } from "@sundaeswap/asset";
import sortBy from "lodash/sortBy";

import { isNaN } from "lodash";
import { Asset, TasteTest, TokenStats } from "../gql/generated/taste-test.sdk";
import { TTasteTestWithDerivedValues } from "../gql/hooks/taste-test.query";
import { IAssetMetaData } from "../types/Asset.types";
import { ADA_METADATA } from "./../constants/cardano.constants";
import { calculateAssetValueInUSD } from "./assets.utils";
import { numberI18n, stringToBigint } from "./number-format";

/**
 * Extracts the taste test ID from a given path string. The function searches for the pattern "/discover/"
 * in the input path and returns the portion of the string that follows it. If the pattern is not found,
 * the function returns null.
 *
 * @param {string} path - The input path string containing the pattern "/discover/" followed by the taste test ID.
 * @returns {string | null} The taste test ID extracted from the path, or null if the pattern is not found.
 *
 * @example
 * const tasteTestPolicyId = getTasteTestPolicyId('/discover/12345'); // Outputs: "12345"
 */
export const getTasteTestPolicyId = (path: string): string => {
  const match = path.match(/\/discover\/(.*)/);
  return match ? match[1] : "";
};

/**
 * Calculate the ADA equivalent for the gathered asset.
 *
 * @param currentBalance The current balance data for the taste test,
 * @returns ADA equivalent for a unit of the gathered asset.
 */
export const calculateTargetPrice = (
  currentBalance?: TokenStats | undefined,
  currentLocale?: string
) => {
  if (!currentBalance) {
    return {
      display: "0",
      raw: 0,
    };
  }

  const tokenSuppliedAmount = new AssetAmount(
    currentBalance.tokenSupplied.amount || 0,
    currentBalance.tokenSupplied.asset
  );
  const tokenGatheredAmount = new AssetAmount(
    currentBalance.tokenGathered.amount || 0,
    currentBalance.tokenGathered.asset
  );
  const ratio = new AssetRatio(tokenGatheredAmount, tokenSuppliedAmount);
  const targetPrice = ratio.getLimitPrice();

  if (isNaN(targetPrice)) {
    return {
      display: "0",
      raw: 0,
    };
  }

  return {
    display: numberI18n(
      targetPrice,
      { minimumFractionDigits: 2, maximumFractionDigits: 10, currency: "USD", style: "currency" },
      currentLocale
    ).replace("$", "₳"),
    raw: targetPrice,
  };
};

/**
 * Calculate the Total Value Locked (TVL) of the pre-sale in ADA when the supplied asset is ADA.
 *
 * @param currentBalance The current balance data for the pre-sale.
 * @returns TVL in ADA.
 */
export const getCurrentBalanceAmounts = (
  currentBalance: TokenStats | undefined,
  adaInUsd: number | undefined
) => {
  if (!currentBalance || !adaInUsd) {
    return {
      currentGatheredBalance: { amount: new AssetAmount(0n), amountUsd: 0 },
      currentSuppliedBalance: { amount: new AssetAmount(0n), amountUsd: 0 },
    };
  }

  const currentGatheredAmount = new AssetAmount<IAssetMetaData>(
    currentBalance.tokenGathered.amount || 0,
    ADA_METADATA
  );
  const currentSuppliedAmount = new AssetAmount<Asset>(
    currentBalance.tokenSupplied.amount || 0,
    currentBalance.tokenSupplied.asset
  );
  const currentGatheredAmountUsd = calculateAssetValueInUSD(
    {
      metadata: ADA_METADATA,
      quantity: currentGatheredAmount.amount,
    },
    adaInUsd
  );

  return {
    currentGatheredBalance: {
      amount: currentGatheredAmount,
      amountUsd: currentGatheredAmountUsd,
    },
    currentSuppliedBalance: {
      amount: currentSuppliedAmount,
      amountUsd: currentGatheredAmountUsd,
    },
  };
};

/**
 * A constant URL string to initiate a mailto: request for launching a Taste Test with the us.
 * @type {string}
 */
export const createTasteTestUrl = `mailto:contact@sundaeswap.finance?subject=Launch%20a%20Taste%20Test&body=Hi%20Sundae%20Swap%20Team%2C%0A%0AI%20would%20like%20to%20launch%20a%20Taste%20Test%20for%20my%20project.%0A%0AHere%20are%20the%20details%3A%0A%0A%0A%0A%0A%0A%0A%0A%0A`;

/**
 * Sorts an array of TasteTest objects in ascending order based on their creation date.
 *
 * @param {TasteTest[]} tasteTests - The array of TasteTest objects to sort.
 * @returns {TasteTest[]} - The sorted array of TasteTest objects.
 */
export const sortTasteTestsByCreatedDate = (tasteTests: TasteTest[] | undefined) =>
  sortBy(tasteTests ?? [], (tasteTest) => tasteTest.createdDate.format);

/**
 * Maps Taste Test data into a richer format containing derived TVL values and asset prices.
 *
 * @param {TasteTest} tt - The original TasteTest data.
 * @param {number} adaInUsd - The current ADA price in USD.
 * @returns {{
 *   ...TasteTest,
 *   contributed: boolean,
 *   currentGatheredBalance: {amount: AssetAmount, amountUsd: number},
 *   currentSuppliedBalance: {amount: AssetAmount, amountUsd: number},
 *   targetPrice: number,
 *   targetPriceUsd: number,
 * }} - The mapped TasteTest data with additional fields.
 */
export const enrichTasteTestWithDerivedValues = (
  tt: TasteTest,
  adaInUsd: number
): TTasteTestWithDerivedValues => {
  const { currentGatheredBalance, currentSuppliedBalance } = getCurrentBalanceAmounts(
    tt.currentBalance,
    adaInUsd
  );
  const targetPrice = calculateTargetPrice(tt.currentBalance);
  const targetPriceAssetAmount = new AssetAmount<IAssetMetaData>(
    stringToBigint(targetPrice.raw, ADA_METADATA.decimals),
    ADA_METADATA
  );
  const targetPriceUsd = calculateAssetValueInUSD(
    {
      metadata: ADA_METADATA,
      quantity: targetPriceAssetAmount.amount,
    },
    adaInUsd
  );
  return {
    ...tt,
    currentGatheredBalance,
    currentSuppliedBalance,
    targetPrice: targetPriceAssetAmount,
    targetPriceUsd,
  };
};

export const getFormattedTargetPrice = () => {};
